import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { fetchUser, fetchUserFollowers, fetchUserFollowing, fetchUserMutuals, fetchFeed } from "@/lib/api";
import { authClient, useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import FollowButton from "@/components/FollowButton";
import MinecraftSkin from "@/components/MinecraftSkin";
import PostCard from "@/components/PostCard";
import UserCard from "@/components/UserCard";
import { sfx } from "@/lib/sounds";
import {
  Youtube, Twitch, Instagram, Twitter, Facebook, Globe, Github,
  Users, MapPin, Cake, Languages, Star, Shield, Sparkles, ExternalLink, Play, MessageCircle, Lock, Send,
} from "lucide-react";

const ICONS = { youtube: Youtube, twitch: Twitch, kick: Play, instagram: Instagram, twitter: Twitter,
                facebook: Facebook, tiktok: Play, discord: MessageCircle, website: Globe, github: Github };
const COLORS = { youtube: "#FF0033", twitch: "#9146FF", kick: "#53FC18", instagram: "#E4405F",
                 twitter: "#1DA1F2", facebook: "#1877F2", tiktok: "#FF0050", discord: "#5865F2",
                 website: "#FFBF00", github: "#ffffff" };

const TABS = [
  { key: "posts", label: "Posts" },
  { key: "builds", label: "Builds" },
  { key: "followers", label: "Followers" },
  { key: "following", label: "Following" },
];

export default function UserProfile() {
  const { id, userId, handle } = useParams();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [u, setU] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [mutuals, setMutuals] = useState([]);

  // Resolve the target user regardless of URL variant.
  //   /users/:id            → id
  //   /community/:userId    → userId
  //   /community/@:handle   → handle (mc_username or display name)
  const lookupTarget = id || userId || (handle ? `@${handle}` : null);

  useEffect(() => {
    if (!lookupTarget) return;
    setErr(null);
    setU(null);
    const path = lookupTarget.startsWith("@")
      ? `/users/${encodeURIComponent(lookupTarget)}`   // /@handle
      : `/users/${encodeURIComponent(lookupTarget)}`;
    fetchUser(lookupTarget).then(setU).catch((e) => {
      console.warn("user fetch failed", e?.message);
      setErr(e?.response?.status === 404 ? "not_found" : "error");
    });
  }, [lookupTarget]);

  useEffect(() => {
    if (!u || u.is_private) return;
    const uid = u.id;
    if (tab === "posts") fetchFeed({ author_id: uid, limit: 20 }).then(d => setPosts(d.posts || [])).catch((e) => console.debug("posts fetch failed", e?.message));
    if (tab === "followers") fetchUserFollowers(uid).then(setFollowers).catch((e) => console.debug("followers fetch failed", e?.message));
    if (tab === "following") fetchUserFollowing(uid).then(setFollowing).catch((e) => console.debug("following fetch failed", e?.message));
  }, [tab, u]);

  useEffect(() => { if (me && u && !u.is_self) fetchUserMutuals(u.id).then(setMutuals).catch((e) => console.debug("mutuals fetch failed", e?.message)); }, [me, u]);

  if (err === "not_found") {
    return (
      <div className="pt-40 pb-24 px-4 max-w-2xl mx-auto text-center" data-testid="user-not-found">
        <div className="rounded-2xl glass p-10">
          <div className="font-hero text-4xl">User not found</div>
          <div className="text-sm text-white/50 mt-2">We couldn’t find <span className="text-[#FFBF00]">{lookupTarget}</span> on the network.</div>
          <button onClick={() => navigate("/community")} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
            Back to Community
          </button>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="pt-40 text-center text-red-400" data-testid="user-error">
        Something went wrong loading this profile. <button onClick={() => window.location.reload()} className="underline">Try again</button>.
      </div>
    );
  }

  if (!u) return <div className="pt-40 text-center text-white/50" data-testid="loading-user">Loading…</div>;

  if (u.is_private) {
    return (
      <div className="pt-40 pb-24 px-4 max-w-2xl mx-auto text-center" data-testid="private-profile">
        <div className="rounded-2xl glass p-10">
          <Lock className="mx-auto mb-3 text-white/40" />
          <img src={u.avatar} alt="" className="w-24 h-24 rounded-2xl mx-auto border border-white/10" />
          <div className="font-hero text-3xl mt-3">{u.name}</div>
          <div className="text-sm text-white/50 mt-2">
            {u.followers_only ? "This profile is only visible to followers." : "This profile is private."}
          </div>
          {me && !u.is_self && u.allow_follows !== false && (
            <div className="mt-6"><FollowButton userId={u.id} initialFollowing={u.is_following} /></div>
          )}
        </div>
      </div>
    );
  }

  const socials = Object.entries(u.social || {}).filter(([, v]) => v);

  return (
    <div className="pt-24 pb-24" data-testid="page-user-profile">
      {/* Banner */}
      <div className="relative h-48 sm:h-72 overflow-hidden">
        {u.banner ? <img src={u.banner} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/30 to-[#FF8C00]/10" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-16 relative">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <img src={u.avatar} alt="" className="w-28 h-28 rounded-2xl border-4 border-black object-cover" data-testid="user-avatar" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">{u.rank || u.role || "Player"}</span>
              {u.is_creator && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FFBF00]/15 border border-[#FFBF00]/40 text-[#FFBF00] font-bold">
                  <Sparkles size={10} /> Creator
                </span>
              )}
              {u.featured_creator && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#FF8C00]/15 border border-[#FF8C00]/40 text-[#FF8C00]">
                  <Star size={10} /> Featured
                </span>
              )}
              {u.role === "admin" && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-400">
                  <Shield size={10} /> Admin
                </span>
              )}
              {u.creator_level > 0 && (
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70">
                  Lvl {u.creator_level}
                </span>
              )}
            </div>
            <div className="font-hero text-4xl sm:text-5xl mt-1">{u.name}</div>
            {u.mc_username && <div className="text-sm font-mono text-[#FFBF00]/80 mt-1">{u.mc_username}</div>}
            {u.tagline && <div className="text-white/60 text-sm italic mt-1">{u.tagline}</div>}
            {u.bio && <p className="text-white/70 text-sm mt-2 max-w-2xl">{u.bio}</p>}
          </div>
          <div className="flex items-center gap-4">
            <Link to={`/users/${u.id}?tab=followers`} onClick={() => setTab("followers")} className="text-center hover:text-[#FFBF00]">
              <div className="font-hero text-2xl text-[#FFBF00]">{(u.followers_count || 0).toLocaleString("en-IN")}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Followers</div>
            </Link>
            <Link to={`/users/${u.id}?tab=following`} onClick={() => setTab("following")} className="text-center hover:text-[#FFBF00]">
              <div className="font-hero text-2xl">{(u.following_count || 0).toLocaleString("en-IN")}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Following</div>
            </Link>
            <div className="text-center">
              <div className="font-hero text-2xl">{(u.posts_count || 0).toLocaleString("en-IN")}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Posts</div>
            </div>
            {me && !u.is_self && u.allow_follows !== false && (
              <FollowButton
                userId={u.id}
                initialFollowing={u.is_following}
                initialPending={u.request_pending}
                onChange={({ followers }) => setU({ ...u, followers_count: followers ?? u.followers_count })} />
            )}
            {me && !u.is_self && (
              <button
                onClick={async () => {
                  const body = window.prompt(`Send a message to ${u.name}`);
                  if (!body || !body.trim()) return;
                  try {
                    const { data } = await authClient.post("/messages/start", { user_id: u.id, body });
                    sfx.pling(); toast.success("Message sent");
                    navigate(`/messages?c=${data.conversation.id}`);
                  } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
                }}
                data-testid="user-message-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-white/15 hover:border-[#FFBF00]/50 text-xs uppercase tracking-widest text-white/80">
                <Send size={12} /> Message
              </button>
            )}
            {u.is_self && (
              <button onClick={() => navigate("/profile")} className="px-5 py-2.5 rounded-full border border-white/20 hover:border-[#FFBF00]/50 text-xs uppercase tracking-widest">
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Meta */}
        <div className="mt-6 flex flex-wrap gap-4 text-xs text-white/60">
          {u.country && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-[#FFBF00]" /> {u.country}</span>}
          {u.location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-[#FF8C00]" /> {u.location}</span>}
          {u.date_of_birth && <span className="inline-flex items-center gap-1.5"><Cake size={12} className="text-[#FFBF00]" /> {u.date_of_birth}</span>}
          {u.preferred_language && <span className="inline-flex items-center gap-1.5"><Languages size={12} /> {u.preferred_language}</span>}
          {u.favorite_server && <span className="inline-flex items-center gap-1.5"><Star size={12} className="text-[#FFBF00]" /> {u.favorite_server}</span>}
          {u.pronouns && <span className="text-white/40">({u.pronouns})</span>}
        </div>

        {/* Socials */}
        {socials.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="user-socials">
            {socials.map(([k, v]) => {
              const I = ICONS[k] || ExternalLink;
              const color = COLORS[k] || "#ffffff";
              const href = /^https?:/.test(v) ? v : `https://${v}`;
              return (
                <a key={k} href={href} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass hover:border-[#FFBF00]/40 text-xs"
                   style={{ borderColor: `${color}33` }}>
                  <I size={13} style={{ color }} />
                  <span className="text-white/80 capitalize">{k}</span>
                </a>
              );
            })}
          </div>
        )}

        {/* Badges */}
        {u.badges?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {u.badges.map((b) => (
              <span key={b} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#FFBF00]/40 bg-[#FFBF00]/10 text-[#FFBF00]">
                {b}
              </span>
            ))}
          </div>
        )}

        {/* Mutuals */}
        {mutuals.length > 0 && (
          <div className="mt-4 text-xs text-white/50">
            Followed by <span className="text-white">{mutuals.slice(0, 3).map(m => m.name).join(", ")}</span>
            {mutuals.length > 3 && <span> and {mutuals.length - 3} others you follow</span>}
          </div>
        )}

        {/* Minecraft */}
        {u.mc_username && (
          <div className="mt-8 rounded-2xl glass p-6 flex items-center gap-6" data-testid="user-mc">
            <MinecraftSkin username={u.mc_username} mode="body" size={140} />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#FFBF00]">Minecraft Identity</div>
              <div className="font-hero text-3xl mt-1">{u.mc_username}</div>
              <div className="text-[11px] font-mono text-white/40 mt-1">UUID · {u.mc_uuid}</div>
              {u.favorite_server && <div className="text-xs text-white/60 mt-2">Favourite server: <span className="text-[#FFBF00]">{u.favorite_server}</span></div>}
              <div className="mt-3 flex gap-2">
                <a href={`https://mc-heads.net/download/${u.mc_username}`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest border border-white/10 hover:border-[#FFBF00]/40">
                  Download skin
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-10 flex gap-1 border-b border-white/10 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); sfx.click(); }}
                    data-testid={`user-tab-${t.key}`}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap ${
                      tab === t.key ? "border-[#FFBF00] text-[#FFBF00]" : "border-transparent text-white/60 hover:text-white"
                    }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "posts" && (
            <div className="space-y-4 max-w-2xl">
              {posts.map((p) => <PostCard key={p.id} post={p} onDelete={(pid) => setPosts((x) => x.filter((q) => q.id !== pid))} />)}
              {posts.length === 0 && <div className="rounded-2xl glass p-10 text-center text-white/50">No posts yet.</div>}
            </div>
          )}
          {tab === "builds" && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(u.latest_builds || []).map((b) => (
                <div key={b.id} className="rounded-xl overflow-hidden border border-white/10">
                  {(b.images?.[0] || b.image) && <img src={b.images?.[0] || b.image} alt="" className="w-full h-40 object-cover" />}
                  <div className="p-3">
                    <div className="text-sm font-semibold line-clamp-1">{b.title}</div>
                    <div className="text-[10px] text-white/40 uppercase tracking-widest">{b.category}</div>
                  </div>
                </div>
              ))}
              {(u.latest_builds || []).length === 0 && <div className="col-span-full rounded-2xl glass p-10 text-center text-white/50">No builds shared yet.</div>}
            </div>
          )}
          {tab === "followers" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {followers.map((f) => <UserCard key={f.id} user={f} compact />)}
              {followers.length === 0 && <div className="col-span-full rounded-2xl glass p-10 text-center text-white/50">No followers yet.</div>}
            </div>
          )}
          {tab === "following" && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {following.map((f) => <UserCard key={f.id} user={f} compact />)}
              {following.length === 0 && <div className="col-span-full rounded-2xl glass p-10 text-center text-white/50">Not following anyone yet.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
